import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { RefVariableDefault } from '../utils/variableTypes';
import { toLabel } from '../utils/variableUtils';
import { Variable } from '../../../services/api/departmentVariablesService';
import { DepartmentUser } from './WizardVariableForm';
import VariableFieldLabel from './WizardFieldLabel';
import { cn } from '../../../lib/utils';
import { Button } from '../../ui/button';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '../../ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';

interface RefVariableFieldProps {
    def: RefVariableDefault;
    current: Variable;
    users: DepartmentUser[];
    isRemovable: boolean;
    onSelectUser: (refId: string) => void;
    onSwitchToExternal: () => void;
    onClearContact: () => void;
    onRemove: () => void;
    onFixedChange: (value: string) => void;
}

export default function RefVariableField({
    def,
    current,
    users,
    isRemovable,
    onSelectUser,
    onClearContact,
    onRemove,
    onFixedChange,
}: RefVariableFieldProps) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');

    const hasValue = !!current.refId || !!current.fixedValue;

    const displayValue = current.refId
        ? users.find((u) => u.id === current.refId)?.name ?? ''
        : current.fixedValue ?? '';

    const filteredUsers = users.filter((u) =>
        u.name.toLowerCase().includes(search.toLowerCase())
    );

    const showFreeTextOption =
        search.trim().length > 0 &&
        !users.some((u) => u.name.toLowerCase() === search.toLowerCase());

    const closePopoverSafely = () => {
        setOpen(false);
        setTimeout(() => setSearch(''), 150);
    };

    const handleSelectUser = (userId: string) => {
        closePopoverSafely();
        setTimeout(() => onSelectUser(userId), 150);
    };

    const handleSelectFreeText = () => {
        const nextValue = search.trim();
        closePopoverSafely();
        setTimeout(() => onFixedChange(nextValue), 150);
    };

    const handleClear = () => {
        isRemovable ? onRemove() : onClearContact();
    };

    const handleOpenChange = (next: boolean) => {
        setOpen(next);
        if (!next) {
            setTimeout(() => setSearch(''), 100);
        }
    };

    return (
        <div className="space-y-1.5">
            <VariableFieldLabel
                label={def.label || toLabel(def.name)}
                required={def.required}
            />
            <div className="flex items-center gap-2">
                <Popover open={open} onOpenChange={handleOpenChange}>
                    <PopoverTrigger asChild>
                        <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={open}
                            className={cn(
                                'flex-1 justify-between font-normal bg-background',
                                !displayValue && 'text-muted-foreground'
                            )}
                        >
                            <span className="truncate">
                                {displayValue || t('departmentVariables.wizardForm.selectOrTypeName')}
                            </span>
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[300px] p-0">
                        <Command shouldFilter={false}>
                            <CommandInput
                                placeholder={t('departmentVariables.wizardForm.searchTeamOrType')}
                                value={search}
                                onValueChange={setSearch}
                            />
                            <CommandList>
                                <CommandEmpty>
                                    {t('departmentVariables.wizardForm.noTeamMembersFound')}
                                </CommandEmpty>
                                {filteredUsers.length > 0 && (
                                    <CommandGroup>
                                        {filteredUsers.map((user) => (
                                            <CommandItem
                                                key={user.id}
                                                value={user.id}
                                                onSelect={() =>
                                                    handleSelectUser(user.id)
                                                }
                                            >
                                                <Check
                                                    className={cn(
                                                        'mr-2 h-4 w-4 shrink-0',
                                                        current.refId ===
                                                            user.id
                                                            ? 'opacity-100'
                                                            : 'opacity-0'
                                                    )}
                                                />
                                                <span>{user.name}</span>
                                                <span className="ml-1.5 text-xs text-muted-foreground">
                                                    {user.role}
                                                </span>
                                            </CommandItem>
                                        ))}
                                    </CommandGroup>
                                )}
                                {showFreeTextOption && (
                                    <CommandGroup>
                                        <CommandItem
                                            value={`__freetext__${search}`}
                                            onSelect={handleSelectFreeText}
                                        >
                                            <span className="text-muted-foreground mr-1">
                                                Use
                                            </span>
                                            &ldquo;{search.trim()}&rdquo;
                                        </CommandItem>
                                    </CommandGroup>
                                )}
                            </CommandList>
                        </Command>
                    </PopoverContent>
                </Popover>
                {(hasValue || isRemovable) && (
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={handleClear}
                        aria-label={`Clear ${def.label || toLabel(def.name)}`}
                        title={`Clear ${def.label || toLabel(def.name)}`}
                    >
                        <X className="h-4 w-4" />
                    </Button>
                )}
            </div>
        </div>
    );
}
